import React, { useState, useRef } from 'react';
import {
  Input,
  Button,
  Stack,
  Flex,
  useTheme,
} from '@fti/design-system';

interface PropertyRow {
  key: string;
  value: string;
  isNew: boolean; // true for rows added by user (key editable)
}

interface PropertyEditorProps {
  /** Initial properties — only read on mount or when initialKey changes */
  properties: Record<string, unknown>;
  /** Called with the full properties object on every edit */
  onChange: (properties: Record<string, unknown>) => void;
  /** Display-only mode */
  readOnly?: boolean;
  /** Change this value to re-initialize rows (e.g., entity element_id) */
  initialKey?: string;
}

function toRows(properties: Record<string, unknown>): PropertyRow[] {
  return Object.entries(properties).map(([key, val]) => ({
    key,
    value: typeof val === 'string' ? val : JSON.stringify(val),
    isNew: false,
  }));
}

function rowsToProperties(rows: PropertyRow[]): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const row of rows) {
    if (!row.key.trim()) continue;
    try {
      result[row.key.trim()] = JSON.parse(row.value);
    } catch {
      result[row.key.trim()] = row.value;
    }
  }
  return result;
}

export const PropertyEditor: React.FC<PropertyEditorProps> = ({ properties, onChange, readOnly = false, initialKey }) => {
  const { theme } = useTheme();
  const [rows, setRows] = useState<PropertyRow[]>(() => toRows(properties));
  const prevInitialKeyRef = useRef(initialKey);

  // Re-initialize rows only when the entity changes (initialKey changes),
  // NOT on every parent re-render from onChange feedback
  if (initialKey !== prevInitialKeyRef.current) {
    prevInitialKeyRef.current = initialKey;
    setRows(toRows(properties));
  }

  const notifyChange = (nextRows: PropertyRow[]) => {
    onChange(rowsToProperties(nextRows));
  };

  const handleValueChange = (index: number, newValue: string) => {
    const next = [...rows];
    next[index] = { ...next[index], value: newValue };
    setRows(next);
    notifyChange(next);
  };

  const handleKeyChange = (index: number, newKey: string) => {
    const next = [...rows];
    next[index] = { ...next[index], key: newKey };
    setRows(next);
    notifyChange(next);
  };

  const handleAddRow = () => {
    const next = [...rows, { key: '', value: '', isNew: true }];
    setRows(next);
    // Don't call notifyChange here — empty key is filtered out anyway
  };

  const handleDeleteRow = (index: number) => {
    const next = rows.filter((_, i) => i !== index);
    setRows(next);
    notifyChange(next);
  };

  // Compute duplicate keys for validation
  const keyCounts: Record<string, number> = {};
  rows.forEach((r) => {
    const k = r.key.trim();
    if (k) keyCounts[k] = (keyCounts[k] || 0) + 1;
  });

  if (readOnly) {
    if (rows.length === 0) {
      return <span style={{ fontSize: '0.8rem', color: theme.colors.text.secondary }}>No properties</span>;
    }
    return (
      <div>
        {rows.map((row) => (
          <div key={row.key} style={{ fontSize: '0.8rem', marginBottom: '2px' }}>
            <span style={{ color: theme.colors.text.secondary }}>{row.key}:</span>{' '}
            <span style={{ color: theme.colors.text.primary }}>{row.value}</span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <Stack gap="xs">
      {rows.map((row, i) => {
        const trimmedKey = row.key.trim();
        const isDuplicate = trimmedKey && (keyCounts[trimmedKey] || 0) > 1;
        const keyError = row.isNew && row.key !== '' && !trimmedKey
          ? 'Key required'
          : isDuplicate
          ? 'Duplicate key'
          : undefined;

        return (
          <Flex key={i} gap="xs" align="start">
            <div style={{ flex: 1, minWidth: 0 }}>
              {row.isNew ? (
                <Input
                  size="sm"
                  placeholder="Key"
                  value={row.key}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleKeyChange(i, e.target.value)}
                  validationState={keyError ? 'error' : 'default'}
                  errorMessage={keyError}
                />
              ) : (
                <span style={{
                  fontSize: '0.8rem',
                  color: theme.colors.text.secondary,
                  lineHeight: '32px',
                  display: 'block',
                }}>{row.key}</span>
              )}
            </div>
            <div style={{ flex: 2, minWidth: 0 }}>
              <Input
                size="sm"
                placeholder="Value"
                value={row.value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleValueChange(i, e.target.value)}
              />
            </div>
            <Button
              variant="ghost"
              color="error"
              size="sm"
              onClick={() => handleDeleteRow(i)}
              style={{ flexShrink: 0, marginTop: '2px' }}
            >
              &times;
            </Button>
          </Flex>
        );
      })}
      <div>
        <Button variant="outline" size="sm" onClick={handleAddRow}>
          + Add Property
        </Button>
      </div>
    </Stack>
  );
};
